import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { HeartHandshake, ClipboardList, Inbox, CheckCircle2, ArrowRight, Loader2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const TYPE_PT = {
  consulta: "Consulta",
  exame: "Exame",
  transporte: "Transporte",
  acompanhante: "Acompanhante",
};

const STATUS_PT = {
  solicitada: "Solicitada",
  em_analise: "Em análise",
  aguardando_profissional: "Aguardando profissional",
  confirmada: "Confirmada",
  realizada: "Realizada",
  cancelada: "Cancelada",
  nao_realizada: "Não realizada",
};

const STATUS_COLOR = {
  solicitada: "bg-amber-50 text-amber-700",
  em_analise: "bg-blue-50 text-blue-700",
  aguardando_profissional: "bg-purple-50 text-purple-700",
  confirmada: "bg-emerald-50 text-emerald-700",
  realizada: "bg-slate-100 text-slate-700",
  cancelada: "bg-red-50 text-red-700",
  nao_realizada: "bg-red-50 text-red-700",
};

const SCOPES = [
  { id: "mine", l: "Minhas solicitações", icon: ClipboardList },
  { id: "queue", l: "Fila (não atribuídas)", icon: Inbox },
];

export default function ConsultantFPInbox() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const load = async (s = scope) => {
    setLoading(true);
    try {
      const [inbox, st] = await Promise.all([
        api.get(`/fp/consultant/inbox?scope=${s}`),
        api.get("/fp/consultant/stats"),
      ]);
      setItems(inbox.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Não foi possível carregar as solicitações");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(scope); /* eslint-disable-next-line */ }, [scope]);

  const assume = async (rid) => {
    try {
      await api.post(`/fp/consultant/${rid}/assume`, {});
      toast.success("Solicitação assumida");
      load(scope);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-consultant-inbox">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
        <HeartHandshake className="w-3.5 h-3.5 text-amber-500" /> Filho Presente · Central do consultor
      </div>
      <h1 className="font-display text-3xl font-bold text-[#0B2B62]">Solicitações Filho Presente</h1>
      <p className="text-slate-600 text-sm mt-1 max-w-2xl">
        Famílias criam solicitações (consultas, exames, transporte, acompanhantes) para seus idosos.
        Você faz a mediação, indica profissional certificado ou clínica parceira e confirma o agendamento.
      </p>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 mb-6">
        <Kpi label="Minhas em aberto" value={stats?.mine_open ?? "—"} accent="#0B2B62" />
        <Kpi label="Minhas confirmadas" value={stats?.mine_confirmed ?? "—"} accent="#059669" />
        <Kpi label="Total minhas" value={stats?.mine_total ?? "—"} />
        <Kpi label="Na fila" value={stats?.queue ?? "—"} accent="#B45309" />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1">
        {SCOPES.map((s) => (
          <button key={s.id} onClick={() => setScope(s.id)} data-testid={`fp-consultor-scope-${s.id}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              scope === s.id ? "border-[#0B2B62] text-[#0B2B62]" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <s.icon className="w-4 h-4" /> {s.l}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-500">Carregando…</div>
        : items.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
            <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="text-slate-700 font-medium">
              {scope === "mine" ? "Você ainda não tem solicitações atribuídas." : "Nenhuma solicitação na fila."}
            </div>
            {scope === "mine" && stats?.queue > 0 && (
              <button onClick={() => setScope("queue")} data-testid="fp-consultor-go-queue"
                className="mt-4 text-sm text-[#0B2B62] font-semibold hover:underline inline-flex items-center gap-1">
                Ver {stats.queue} na fila <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Beneficiário</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Detalhe</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Data desejada</th>
                  <th className="text-right px-4 py-3">Ação</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`fp-req-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0B2B62]">{TYPE_PT[r.type] || r.type}</div>
                      <div className="text-[10px] text-slate-500">
                        Solicitada {new Date(r.created_at).toLocaleDateString("pt-BR")}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{r.beneficiary_name}</div>
                      <div className="text-xs text-slate-500">Família: {r.family_name || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 hidden md:table-cell text-xs">
                      {r.specialty || r.exam_type || r.destination || r.location || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLOR[r.status] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_PT[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 hidden sm:table-cell">
                      {r.desired_date || r.date || "—"}
                      {r.desired_time && <> · {r.desired_time}</>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {scope === "queue" ? (
                        <button onClick={() => assume(r.id)} data-testid={`fp-assume-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs bg-[#0B2B62] text-white px-3 py-1.5 rounded-md font-semibold hover:bg-slate-800">
                          Assumir <ArrowRight className="w-3 h-3" />
                        </button>
                      ) : r.status === "confirmada" ? (
                        <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Confirmada
                        </span>
                      ) : (
                        <button onClick={() => setConfirmTarget(r)} data-testid={`fp-confirm-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-md font-semibold hover:bg-emerald-700">
                          Confirmar mediação
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {confirmTarget && (
        <ConfirmMediationModal req={confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onDone={() => { setConfirmTarget(null); load(scope); }} />
      )}
    </div>
  );
}

const Kpi = ({ label, value, accent = "#0B2B62" }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className="font-display font-bold text-3xl mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const ConfirmMediationModal = ({ req, onClose, onDone }) => {
  const [pros, setPros] = useState([]);
  const [form, setForm] = useState({
    professional_id: req.professional_id || "",
    scheduled_at: req.scheduled_at ? req.scheduled_at.slice(0, 16) : "",
    modality: req.modality || "presencial",
    provider_snapshot: "",
    notes: "",
    family_terms_accepted: false,
    create_booking: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (req.type !== "consulta") return;
    api.get(`/fp/professionals?category=${encodeURIComponent(req.specialty || "")}`)
      .then((r) => setPros(r.data)).catch(() => {});
  }, [req]);

  const selectedPro = useMemo(
    () => pros.find((p) => p.id === form.professional_id) || null,
    [pros, form.professional_id],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (form.create_booking && !form.family_terms_accepted) {
      return toast.error("Confirme que a família aceitou as regras antes de gerar o link de pagamento.");
    }
    setSaving(true);
    try {
      const payload = {
        professional_id: form.professional_id || null,
        professional_name: selectedPro?.display_name || null,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        modality: form.modality,
        provider_snapshot: form.provider_snapshot || null,
        notes: form.notes || null,
        family_terms_accepted: form.family_terms_accepted,
        create_booking: form.create_booking,
        origin_url: window.location.origin,
      };
      const { data } = await api.post(`/fp/consultant/${req.id}/confirm`, payload);
      if (data.checkout_url) {
        toast.success("Mediação confirmada e link de pagamento gerado");
        window.open(data.checkout_url, "_blank");
      } else {
        toast.success("Mediação confirmada");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    } finally { setSaving(false); }
  };

  const isConsulta = req.type === "consulta";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="fp-confirm-modal"
        className="bg-white rounded-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
              Confirmar mediação · {TYPE_PT[req.type]}
            </div>
            <div className="font-display text-xl font-bold text-[#0B2B62] mt-1">{req.beneficiary_name}</div>
            <div className="text-xs text-slate-500">Família: {req.family_name || "—"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isConsulta && (
          <>
            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Profissional certificado</label>
            <select value={form.professional_id} onChange={(e) => setForm({ ...form, professional_id: e.target.value })}
              data-testid="fp-confirm-pro"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3">
              <option value="">— selecione —</option>
              {pros.map((p) => (
                <option key={p.id} value={p.id}>{p.display_name} · {p.category || (p.categories || [])[0] || ""}</option>
              ))}
            </select>

            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Data e hora</label>
            <input type="datetime-local" value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              data-testid="fp-confirm-datetime"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3" />

            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Modalidade</label>
            <select value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3">
              <option value="presencial">Presencial</option>
              <option value="online">Online</option>
              <option value="domiciliar">Domiciliar</option>
            </select>
          </>
        )}

        {!isConsulta && (
          <>
            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
              Prestador / clínica indicada
            </label>
            <input type="text" value={form.provider_snapshot}
              onChange={(e) => setForm({ ...form, provider_snapshot: e.target.value })}
              placeholder="Ex: Laboratório XYZ, Cel 11 99999-9999"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3" />
          </>
        )}

        <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Observações internas</label>
        <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Instruções para a família ou anotações internas."
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3" />

        {isConsulta && form.professional_id && form.scheduled_at && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-3">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.create_booking}
                onChange={(e) => setForm({ ...form, create_booking: e.target.checked })}
                data-testid="fp-confirm-booking-flag" className="mt-1" />
              <span className="text-xs text-emerald-900 font-semibold">
                Gerar link de pagamento (Stripe/PIX) — comissão do consultor será registrada automaticamente (10% da fatia RAF).
              </span>
            </label>
            {form.create_booking && (
              <label className="mt-2 inline-flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.family_terms_accepted}
                  onChange={(e) => setForm({ ...form, family_terms_accepted: e.target.checked })}
                  data-testid="fp-confirm-terms" className="mt-1" />
                <span className="text-[11px] text-emerald-800">
                  Confirmo que apresentei as regras (cancelamento 24h, não comparecimento, feriados) à família e obtive consentimento.
                </span>
              </label>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="fp-confirm-submit"
            className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-md text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar mediação
          </button>
        </div>
      </form>
    </div>
  );
};
